/**
 * RFC 7807 ProblemDetails — standard error response from the API.
 */
export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

/**
 * Parse an Axios error response into a ProblemDetails object.
 * Falls back to a generic problem if the response doesn't match.
 */
export function parseProblemDetails(data: unknown, status: number): ProblemDetails {
  if (
    data &&
    typeof data === 'object' &&
    'title' in data &&
    'status' in data
  ) {
    return data as ProblemDetails;
  }

  // Fallback for non-ProblemDetails errors
  return {
    type: 'https://tools.ietf.org/html/rfc7807',
    title: statusToTitle(status),
    status,
    detail:
      data && typeof data === 'object' && 'message' in data
        ? String((data as Record<string, unknown>).message)
        : undefined,
  };
}

/** Returns a validation message regardless of ASP.NET Pascal/camel casing. */
export function getValidationError(
  problem: ProblemDetails | undefined,
  fieldName: string,
): string | undefined {
  const errors = problem?.errors;
  if (!errors) return undefined;

  const normalized = fieldName.toLocaleLowerCase('en-US');
  const entry = Object.entries(errors).find(
    ([key]) => key.toLocaleLowerCase('en-US') === normalized,
  );
  return entry?.[1]?.[0];
}

function statusToTitle(status: number): string {
  const map: Record<number, string> = {
    400: 'Requisição inválida',
    401: 'Não autenticado',
    403: 'Acesso negado',
    404: 'Recurso não encontrado',
    409: 'Conflito',
    422: 'Dados inválidos',
    429: 'Muitas requisições',
    500: 'Erro interno do servidor',
    502: 'Provedor indisponível',
    503: 'Serviço indisponível',
  };
  return map[status] || `Erro ${status}`;
}

/**
 * Custom error class wrapping ProblemDetails for typed catch blocks.
 */
export class ApiError extends Error {
  public readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail || problem.title);
    this.name = 'ApiError';
    this.problem = problem;
  }

  get status() {
    return this.problem.status;
  }

  get errors() {
    return this.problem.errors;
  }
}
